import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Button, Flex, H5, Icon, Loading, M, Tooltip } from 'design-system-web';
import { Alert } from 'components/lds';
import { createTargetTimeRule, searchTargetTimeRules } from 'graphql-queries';
import { WorkOrderModuleCategoriesSettings } from 'lane-shared/domains/workOrder';
import { Channel } from 'lane-shared/types/ChannelType';
import { UserType } from 'lane-shared/types/User';

import { TargetTimesCreateModal } from './TargetTimesCreateModal';
import { formatTargetDurationSeconds } from './targetTimesFormat';
import { parseTargetTimeModalSubmit } from './targetTimesModalSubmit';
import styles from './TargetTimesTab.scss';

const LIST_PAGINATION = { start: 0, perPage: 500 };

const ACTIONS_PLACEHOLDER = '\u2014';

/** Dropdown value: create one rule per issue in the selected category. */
const ALL_ISSUES_VALUE = '__TARGET_TIMES_ALL_ISSUES__';

/** Dropdown value: retry every issue that failed on the last partial submit. */
const FAILED_RETRY_BATCH_VALUE = '__TARGET_TIMES_FAILED_RETRY_BATCH__';

async function createTargetTimeRulesSequential(
  createRule: (opts: {
    variables: {
      input: {
        channelId: string;
        category: string;
        issue: string;
        targetResponseSeconds: number;
        targetCompletionSeconds: number;
      };
    };
  }) => Promise<unknown>,
  params: {
    channelId: string;
    category: string;
    issues: string[];
    targetResponseSeconds: number;
    targetCompletionSeconds: number;
  }
): Promise<{
  successCount: number;
  failures: string[];
  failedIssues: string[];
}> {
  const failures: string[] = [];
  const failedIssues: string[] = [];
  let successCount = 0;

  for (const issue of params.issues) {
    try {
      await createRule({
        variables: {
          input: {
            channelId: params.channelId,
            category: params.category,
            issue,
            targetResponseSeconds: params.targetResponseSeconds,
            targetCompletionSeconds: params.targetCompletionSeconds,
          },
        },
      });
      successCount += 1;
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);

      failedIssues.push(issue);
      failures.push(`${issue}: ${message}`);
    }
  }

  return { successCount, failures, failedIssues };
}

function showCreateResultToast(
  t: TFunction,
  successCount: number,
  failuresLength: number
) {
  if (successCount === 0) {
    return;
  }

  if (successCount === 1 && failuresLength === 0) {
    window.Toast.show(
      t('web.admin.serviceRequest.settings.targetTimes.toast.created')
    );

    return;
  }

  if (failuresLength === 0) {
    window.Toast.show(
      t('web.admin.serviceRequest.settings.targetTimes.toast.createdMany', {
        count: successCount,
      })
    );

    return;
  }

  window.Toast.show(
    t('web.admin.serviceRequest.settings.targetTimes.toast.createdPartial', {
      ok: successCount,
      failed: failuresLength,
    })
  );
}

type TargetTimeSubmitFlowParams = {
  createRule: (opts: {
    variables: {
      input: {
        channelId: string;
        category: string;
        issue: string;
        targetResponseSeconds: number;
        targetCompletionSeconds: number;
      };
    };
  }) => Promise<unknown>;
  refetch: (vars: {
    channelId: string;
    pagination: { start: number; perPage: number };
  }) => Promise<unknown>;
  listVariables: {
    channelId: string;
    pagination: { start: number; perPage: number };
  };
  channelId: string;
  category: string;
  issues: string[];
  targetResponseSeconds: number;
  targetCompletionSeconds: number;
  t: TFunction;
  setFormError: (msg: string | null) => void;
  handleCloseModal: () => void;
};

async function runTargetTimeSubmitFlow(
  params: TargetTimeSubmitFlowParams
): Promise<{
  successCount: number;
  failures: string[];
  failedIssues: string[];
  refetchFailed: boolean;
}> {
  const { successCount, failures, failedIssues } =
    await createTargetTimeRulesSequential(params.createRule, {
      channelId: params.channelId,
      category: params.category,
      issues: params.issues,
      targetResponseSeconds: params.targetResponseSeconds,
      targetCompletionSeconds: params.targetCompletionSeconds,
    });

  let refetchFailed = false;

  try {
    await params.refetch(params.listVariables);
  } catch {
    refetchFailed = true;
  }

  if (failures.length > 0) {
    params.setFormError(
      params.t(
        'web.admin.serviceRequest.settings.targetTimes.form.validation.createFailed',
        {
          details: failures.join(' '),
        }
      )
    );
  } else if (refetchFailed) {
    params.setFormError(
      params.t(
        'web.admin.serviceRequest.settings.targetTimes.form.validation.refreshFailed'
      )
    );
  }

  showCreateResultToast(params.t, successCount, failures.length);

  if (failures.length === 0 && !refetchFailed) {
    params.handleCloseModal();
  }

  return { successCount, failures, failedIssues, refetchFailed };
}

function syncPendingRetryIssuesAfterSubmit(options: {
  flowResult: {
    failedIssues: string[];
    refetchFailed: boolean;
    successCount: number;
  };
  setPendingRetryIssues: (issues: string[] | null) => void;
  setIssueValue: (value: string) => void;
  setPendingRefetchRetry: (value: boolean) => void;
  failedRetryBatchSentinel: string;
}): void {
  const {
    flowResult,
    setPendingRetryIssues,
    setIssueValue,
    setPendingRefetchRetry,
    failedRetryBatchSentinel,
  } = options;

  if (flowResult.failedIssues.length > 0) {
    setPendingRetryIssues(flowResult.failedIssues);
    setPendingRefetchRetry(false);

    if (flowResult.failedIssues.length === 1) {
      setIssueValue(flowResult.failedIssues[0]);
    } else {
      setIssueValue(failedRetryBatchSentinel);
    }

    return;
  }

  setPendingRetryIssues(null);

  if (flowResult.refetchFailed && flowResult.successCount > 0) {
    setPendingRefetchRetry(true);
  } else {
    setPendingRefetchRetry(false);
  }
}

type TargetTimesModalSubmitContext = {
  submitGuardRef: React.MutableRefObject<boolean>;
  pendingRefetchRetry: boolean;
  categoryValue: string | null;
  issueValue: string;
  respHours: string;
  respMinutes: string;
  compHours: string;
  compMinutes: string;
  categories: WorkOrderModuleCategoriesSettings[];
  pendingRetryIssues: string[] | null;
  issuesWithExistingRulesForCategory: string[];
  failedRetryBatchSentinel: string;
  channelId: string;
  createRule: TargetTimeSubmitFlowParams['createRule'];
  refetch: TargetTimeSubmitFlowParams['refetch'];
  listVariables: TargetTimeSubmitFlowParams['listVariables'];
  setFormError: (msg: string | null) => void;
  handleCloseModal: () => void;
  setSubmitInFlight: (value: boolean) => void;
  setPendingRetryIssues: (issues: string[] | null) => void;
  setPendingRefetchRetry: (value: boolean) => void;
  setIssueValue: (value: string) => void;
  t: TFunction;
};

async function runRefetchOnlyModalRetry(ctx: TargetTimesModalSubmitContext): Promise<void> {
  ctx.submitGuardRef.current = true;

  try {
    ctx.setSubmitInFlight(true);
    ctx.setFormError(null);

    try {
      await ctx.refetch(ctx.listVariables);
    } catch {
      ctx.setFormError(
        ctx.t(
          'web.admin.serviceRequest.settings.targetTimes.form.validation.refreshFailed'
        )
      );

      return;
    }

    ctx.setPendingRefetchRetry(false);
    ctx.handleCloseModal();
  } finally {
    ctx.submitGuardRef.current = false;
    ctx.setSubmitInFlight(false);
  }
}

async function runTargetTimesModalSubmit(
  ctx: TargetTimesModalSubmitContext
): Promise<void> {
  if (ctx.submitGuardRef.current) {
    return;
  }

  if (ctx.pendingRefetchRetry) {
    await runRefetchOnlyModalRetry(ctx);

    return;
  }

  ctx.setFormError(null);

  const parsedSubmit = parseTargetTimeModalSubmit({
    categoryValue: ctx.categoryValue,
    issueValue: ctx.issueValue,
    respHours: ctx.respHours,
    respMinutes: ctx.respMinutes,
    compHours: ctx.compHours,
    compMinutes: ctx.compMinutes,
    categories: ctx.categories,
    allIssuesSentinel: ALL_ISSUES_VALUE,
    issuesWithExistingRules: ctx.issuesWithExistingRulesForCategory,
    failedRetryBatchSentinel: ctx.failedRetryBatchSentinel,
    pendingRetryIssues: ctx.pendingRetryIssues,
    t: ctx.t,
  });

  if (!parsedSubmit.ok) {
    ctx.setFormError(parsedSubmit.error);

    return;
  }

  const issuesToCreate = parsedSubmit.issues;

  ctx.submitGuardRef.current = true;

  try {
    ctx.setSubmitInFlight(true);

    const flowResult = await runTargetTimeSubmitFlow({
      createRule: ctx.createRule,
      refetch: ctx.refetch,
      listVariables: ctx.listVariables,
      channelId: ctx.channelId,
      category: parsedSubmit.category,
      issues: issuesToCreate,
      targetResponseSeconds: parsedSubmit.targetResponseSeconds,
      targetCompletionSeconds: parsedSubmit.targetCompletionSeconds,
      t: ctx.t,
      setFormError: ctx.setFormError,
      handleCloseModal: ctx.handleCloseModal,
    });

    syncPendingRetryIssuesAfterSubmit({
      flowResult,
      setPendingRetryIssues: ctx.setPendingRetryIssues,
      setIssueValue: ctx.setIssueValue,
      setPendingRefetchRetry: ctx.setPendingRefetchRetry,
      failedRetryBatchSentinel: ctx.failedRetryBatchSentinel,
    });
  } catch (e: unknown) {
    ctx.setFormError(e instanceof Error ? e.message : String(e));
  } finally {
    ctx.submitGuardRef.current = false;
    ctx.setSubmitInFlight(false);
  }
}

type Props = {
  channel: Channel;
  user: UserType;
  categories: WorkOrderModuleCategoriesSettings[];
};

type RuleRow = {
  id: string;
  category: string;
  issue: string;
  targetResponseSeconds: number;
  targetCompletionSeconds: number;
};

type TargetTimeDurationFields = {
  respHours: string;
  respMinutes: string;
  compHours: string;
  compMinutes: string;
};

const INITIAL_TARGET_TIME_DURATIONS: TargetTimeDurationFields = {
  respHours: '0',
  respMinutes: '0',
  compHours: '0',
  compMinutes: '0',
};

function TargetTimesRuleTable({ rules }: { rules: RuleRow[] }) {
  const { t } = useTranslation();

  const headerWithTooltip = (labelKey: string, tooltipKey: string) => (
    <span className={styles.thLabel}>
      {t(labelKey)}
      <Tooltip TooltipComponent={t(tooltipKey)}>
        <button
          type="button"
          className={styles.infoTrigger}
          aria-label={t(tooltipKey)}
        >
          <Icon name="info-circle" set="FontAwesome" type="far" />
        </button>
      </Tooltip>
    </span>
  );

  if (rules.length === 0) {
    return <M>{t('web.admin.serviceRequest.settings.targetTimes.empty')}</M>;
  }

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.th}>
              {t('web.admin.serviceRequest.settings.targetTimes.table.category')}
            </th>
            <th className={styles.th}>
              {t('web.admin.serviceRequest.settings.targetTimes.table.issue')}
            </th>
            <th className={styles.th}>
              {headerWithTooltip(
                'web.admin.serviceRequest.settings.targetTimes.table.responseTime',
                'web.admin.serviceRequest.settings.targetTimes.tooltip.response'
              )}
            </th>
            <th className={styles.th}>
              {headerWithTooltip(
                'web.admin.serviceRequest.settings.targetTimes.table.completionTime',
                'web.admin.serviceRequest.settings.targetTimes.tooltip.completion'
              )}
            </th>
            <th className={`${styles.th} ${styles.thActions}`}>
              {t('web.admin.serviceRequest.settings.targetTimes.table.actions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rules.map(rule => (
            <tr key={rule.id}>
              <td className={styles.td}>{rule.category}</td>
              <td className={styles.td}>{rule.issue}</td>
              <td className={styles.td}>
                {formatTargetDurationSeconds(rule.targetResponseSeconds)}
              </td>
              <td className={styles.td}>
                {formatTargetDurationSeconds(rule.targetCompletionSeconds)}
              </td>
              <td className={`${styles.td} ${styles.tdActions}`}>
                <span className={styles.actionsPlaceholder}>
                  {ACTIONS_PLACEHOLDER}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type TargetTimesFormOptionsInput = {
  categories: WorkOrderModuleCategoriesSettings[];
  categoryValue: string | null;
  rules: RuleRow[];
  pendingRetryIssues: string[] | null;
  t: TFunction;
};

function countCreatableIssuesForCategory(
  category: WorkOrderModuleCategoriesSettings,
  rules: RuleRow[]
): number {
  const name = category.firendlyName ?? '';

  if (name.trim() === '') {
    return 0;
  }

  const issueStrings = (category.issues ?? []).filter(
    i => (i ?? '').trim() !== ''
  );

  if (issueStrings.length === 0) {
    return 0;
  }

  const existingIssues = new Set(
    rules.filter(r => r.category === name).map(r => r.issue)
  );

  return issueStrings.filter(issue => !existingIssues.has(issue)).length;
}

function useTargetTimesFormOptions({
  categories,
  categoryValue,
  rules,
  pendingRetryIssues,
  t,
}: TargetTimesFormOptionsInput) {
  const categoryItems = useMemo(
    () =>
      categories
        .filter(c => (c.firendlyName ?? '').trim() !== '')
        .filter(c => countCreatableIssuesForCategory(c, rules) > 0)
        .map(c => ({
          label: c.firendlyName,
          value: c.firendlyName,
        })),
    [categories, rules]
  );

  const selectedCategory = useMemo(
    () =>
      categoryValue
        ? categories.find(c => c.firendlyName === categoryValue)
        : undefined,
    [categories, categoryValue]
  );

  const issuesWithExistingRulesForCategory = useMemo(() => {
    if (!categoryValue) {
      return [];
    }

    return rules
      .filter(r => r.category === categoryValue)
      .map(r => r.issue);
  }, [rules, categoryValue]);

  const issueItems = useMemo(() => {
    if (!selectedCategory) {
      return [];
    }

    const issueStrings = (selectedCategory.issues ?? []).filter(
      i => (i ?? '').trim() !== ''
    );
    const existingSet = new Set(issuesWithExistingRulesForCategory);
    const creatableIssues = issueStrings.filter(issue => !existingSet.has(issue));
    const pending = (pendingRetryIssues ?? []).filter(Boolean);

    if (pending.length > 1) {
      const stillFailed = pending.filter(issue => !existingSet.has(issue));

      if (stillFailed.length === 0) {
        return [];
      }

      return [
        {
          label: t(
            'web.admin.serviceRequest.settings.targetTimes.form.retryFailedIssues',
            { count: stillFailed.length }
          ),
          value: FAILED_RETRY_BATCH_VALUE,
        },
        ...stillFailed.map(issue => ({ label: issue, value: issue })),
      ];
    }

    if (creatableIssues.length === 0) {
      return [];
    }

    if (creatableIssues.length === 1) {
      const only = creatableIssues[0];

      return [{ label: only, value: only }];
    }

    return [
      {
        label: t('web.admin.serviceRequest.settings.targetTimes.form.allIssues'),
        value: ALL_ISSUES_VALUE,
      },
      ...creatableIssues.map(issue => ({ label: issue, value: issue })),
    ];
  }, [
    selectedCategory,
    issuesWithExistingRulesForCategory,
    pendingRetryIssues,
    t,
  ]);

  return { categoryItems, issueItems, issuesWithExistingRulesForCategory };
}

type IssueDropdownAlignmentInput = {
  modalOpen: boolean;
  categoryValue: string | null;
  issueItems: { value: string }[];
  issueValue: string;
  setIssueValue: (value: string) => void;
};

function useTargetTimesIssueDropdownAlignment(input: IssueDropdownAlignmentInput) {
  const { modalOpen, categoryValue, issueItems, issueValue, setIssueValue } = input;

  useEffect(() => {
    if (!modalOpen || !categoryValue) {
      return;
    }

    const allowed = new Set(issueItems.map(item => item.value));

    if (allowed.size === 0) {
      return;
    }

    if (!allowed.has(issueValue)) {
      setIssueValue(issueItems[0].value);
    }
  }, [modalOpen, categoryValue, issueItems, issueValue, setIssueValue]);
}

type CategoryDropdownAlignmentInput = {
  modalOpen: boolean;
  categoryValue: string | null;
  categoryItems: { value: string }[];
  setCategoryValue: (value: string | null) => void;
  setIssueValue: (value: string) => void;
  setPendingRetryIssues: (issues: string[] | null) => void;
};

function useTargetTimesCategoryDropdownAlignment(
  input: CategoryDropdownAlignmentInput
) {
  const {
    modalOpen,
    categoryValue,
    categoryItems,
    setCategoryValue,
    setIssueValue,
    setPendingRetryIssues,
  } = input;

  useEffect(() => {
    if (!modalOpen || !categoryValue) {
      return;
    }

    const stillAllowed = categoryItems.some(c => c.value === categoryValue);

    if (!stillAllowed) {
      setCategoryValue(null);
      setIssueValue(ALL_ISSUES_VALUE);
      setPendingRetryIssues(null);
    }
  }, [
    modalOpen,
    categoryValue,
    categoryItems,
    setCategoryValue,
    setIssueValue,
    setPendingRetryIssues,
  ]);
}

function useTargetTimesRulesQuery(channelId: string) {
  const listVariables = useMemo(
    () => ({
      channelId,
      pagination: LIST_PAGINATION,
    }),
    [channelId]
  );

  const { data, loading, error, refetch } = useQuery(searchTargetTimeRules, {
    variables: listVariables,
    fetchPolicy: 'network-only',
  });

  const [createRule, { loading: saving }] = useMutation(createTargetTimeRule);

  const rules: RuleRow[] = data?.searchTargetTimeRules?.targetTimeRules ?? [];

  return { listVariables, data, loading, error, refetch, createRule, saving, rules };
}

function TargetTimesTabLoadingShell({
  loading,
  data,
  children,
}: {
  loading: boolean;
  data: unknown;
  children: React.ReactElement;
}) {
  if (loading && !data) {
    return <Loading />;
  }

  return children;
}

function useTargetTimesModalRetryState() {
  const [pendingRetryIssues, setPendingRetryIssues] = useState<string[] | null>(
    null
  );
  const [pendingRefetchRetry, setPendingRefetchRetry] = useState(false);

  const clearRetryFlags = useCallback(() => {
    setPendingRetryIssues(null);
    setPendingRefetchRetry(false);
  }, []);

  const clearPendingRetryIssuesOnly = useCallback(() => {
    setPendingRetryIssues(null);
  }, []);

  return {
    pendingRetryIssues,
    setPendingRetryIssues,
    pendingRefetchRetry,
    setPendingRefetchRetry,
    clearRetryFlags,
    clearPendingRetryIssuesOnly,
  };
}

export function TargetTimesTab({ channel, categories }: Props) {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [categoryValue, setCategoryValue] = useState<string | null>(null);
  const [issueValue, setIssueValue] = useState<string>(ALL_ISSUES_VALUE);
  const [durations, setDurations] = useState<TargetTimeDurationFields>({
    ...INITIAL_TARGET_TIME_DURATIONS,
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [submitInFlight, setSubmitInFlight] = useState(false);
  const submitGuardRef = useRef(false);
  const {
    pendingRetryIssues,
    setPendingRetryIssues,
    pendingRefetchRetry,
    setPendingRefetchRetry,
    clearRetryFlags,
    clearPendingRetryIssuesOnly,
  } = useTargetTimesModalRetryState();

  const {
    listVariables,
    data,
    loading,
    error,
    refetch,
    createRule,
    saving,
    rules,
  } = useTargetTimesRulesQuery(channel._id);

  const { categoryItems, issueItems, issuesWithExistingRulesForCategory } =
    useTargetTimesFormOptions({
      categories,
      categoryValue,
      rules,
      pendingRetryIssues,
      t,
    });

  useTargetTimesIssueDropdownAlignment({
    modalOpen,
    categoryValue,
    issueItems,
    issueValue,
    setIssueValue,
  });

  useTargetTimesCategoryDropdownAlignment({
    modalOpen,
    categoryValue,
    categoryItems,
    setCategoryValue,
    setIssueValue,
    setPendingRetryIssues,
  });

  const resetForm = useCallback(() => {
    setCategoryValue(null);
    setIssueValue(ALL_ISSUES_VALUE);
    setDurations({ ...INITIAL_TARGET_TIME_DURATIONS });
    setFormError(null);
    clearRetryFlags();
    submitGuardRef.current = false;
  }, [clearRetryFlags]);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    resetForm();
  }, [resetForm]);

  const onCategoryChange = useCallback(
    (value: string) => {
      setCategoryValue(value);
      setIssueValue(ALL_ISSUES_VALUE);
      clearPendingRetryIssuesOnly();
    },
    [clearPendingRetryIssuesOnly]
  );

  const onIssueChange = useCallback(
    (value: string) => {
      setIssueValue(value);

      if (value !== FAILED_RETRY_BATCH_VALUE) {
        clearPendingRetryIssuesOnly();
      }
    },
    [clearPendingRetryIssuesOnly]
  );

  const handleSubmit = useCallback(async () => {
    await runTargetTimesModalSubmit({
      submitGuardRef,
      pendingRefetchRetry,
      categoryValue,
      issueValue,
      respHours: durations.respHours,
      respMinutes: durations.respMinutes,
      compHours: durations.compHours,
      compMinutes: durations.compMinutes,
      categories,
      pendingRetryIssues,
      issuesWithExistingRulesForCategory,
      failedRetryBatchSentinel: FAILED_RETRY_BATCH_VALUE,
      channelId: channel._id,
      createRule,
      refetch,
      listVariables,
      setFormError,
      handleCloseModal,
      setSubmitInFlight,
      setPendingRetryIssues,
      setPendingRefetchRetry,
      setIssueValue,
      t,
    });
  }, [
    categories,
    categoryValue,
    channel._id,
    createRule,
    durations.compHours,
    durations.compMinutes,
    durations.respHours,
    durations.respMinutes,
    handleCloseModal,
    issueValue,
    issuesWithExistingRulesForCategory,
    listVariables,
    pendingRefetchRetry,
    pendingRetryIssues,
    refetch,
    setPendingRefetchRetry,
    setPendingRetryIssues,
    t,
  ]);

  return (
    <TargetTimesTabLoadingShell loading={loading} data={data}>
      <Flex direction="column" gap={6} className={styles.targetTimesTab}>
      <div className={styles.sectionIntro}>
        <div className={styles.sectionHeader}>
          <H5 className={styles.sectionTitle}>
            {t`web.admin.serviceRequest.settings.tabs.targetTimes`}
          </H5>
          <Button
            variant="primary"
            size="large"
            className={styles.createButton}
            testId="target-times-create"
            onClick={() => {
              resetForm();
              setModalOpen(true);
            }}
          >
            {t`web.admin.serviceRequest.settings.targetTimes.addRule`}
          </Button>
        </div>
        <M className={styles.sectionDescription}>
          {t('web.admin.serviceRequest.settings.targetTimes.description')}
        </M>
      </div>

      {error ? (
        <Alert type="error">
          {t('web.admin.serviceRequest.settings.targetTimes.error.load')}
        </Alert>
      ) : null}

      <TargetTimesRuleTable rules={rules} />

      <TargetTimesCreateModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        formError={formError}
        categoryItems={categoryItems}
        categoryValue={categoryValue}
        onCategoryChange={onCategoryChange}
        categoryDisabled={categoryItems.length === 0}
        issueItems={issueItems}
        issueDropdownValue={categoryValue ? issueValue : null}
        onIssueChange={onIssueChange}
        issueDisabled={!categoryValue || issueItems.length === 0}
        respHours={durations.respHours}
        onRespHoursChange={v =>
          setDurations(prev => ({ ...prev, respHours: v }))
        }
        respMinutes={durations.respMinutes}
        onRespMinutesChange={v =>
          setDurations(prev => ({ ...prev, respMinutes: v }))
        }
        compHours={durations.compHours}
        onCompHoursChange={v =>
          setDurations(prev => ({ ...prev, compHours: v }))
        }
        compMinutes={durations.compMinutes}
        onCompMinutesChange={v =>
          setDurations(prev => ({ ...prev, compMinutes: v }))
        }
        submitLoading={saving || submitInFlight}
        onSubmit={() => {
          handleSubmit();
        }}
      />
    </Flex>
    </TargetTimesTabLoadingShell>
  );
}
